import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router";
import { useStaffWorkspace, type StaffWorkspace } from "../../app/session";
import { api } from "../../data";
import { Loading } from "../../components/States";
import { ChefUebersicht } from "./uebersicht/ChefUebersicht";
import { ManagerAnsicht } from "./uebersicht/ManagerAnsicht";
import { StudioUebersicht } from "./uebersicht/StudioUebersicht";
import { gemerkt, zumSchnellstart } from "./schnellstart-daten";
import { useNurLesen } from "../../app/nurLesen";
import { useRundgang } from "./coco-rundgang";

/**
 * Übersicht (11_app_agentur/dashboard.html). Admins und Manager mit dem Recht „Kennzahlen und Agenturumsatz sehen“
 * bekommen die Kennzahlen; alle anderen „Dein Tagesgeschäft“ (manager-ansicht.html).
 *
 * Aktivierungsplan Vertrieb 16.10.2026: Ein Admin einer Agentur ohne eigenen Creator und ohne Beispieldaten landet statt auf der
 * leeren Übersicht im Schnellstart (nach Registrierung, Bestätigungslink, übersprungener Zahlungsart oder Rückkehr von Stripe).
 * „Später, zur Übersicht“ im Schnellstart schaltet das für diesen Browser ab.
 */
export function Uebersicht() {
  const ws = useStaffWorkspace();
  const nurLesen = useNurLesen();
  // Creator-Konto (0178): eigene Zahlen statt Agenturanteil
  if (ws.isCreator) return <StudioUebersicht ws={ws} />;
  // Befund 5b (21.10.2026): nur lesbar (Test beendet, gekündigt): kein Schnellstart „Mit eigenen Daten starten“
  if (ws.isChef && !nurLesen && !gemerkt(ws.org.org_id, "spaeter")) return <MitSchnellstartWeiche ws={ws} />;
  return ws.can("view_financials") ? <ChefUebersicht ws={ws} /> : <ManagerAnsicht ws={ws} />;
}

function MitSchnellstartWeiche({ ws }: { ws: StaffWorkspace }) {
  const lauf = useRundgang();
  const stand = useQuery({
    queryKey: ["schnellstart", ws.org.org_id, "weiche"], staleTime: 30_000, retry: false,
    queryFn: () => api.read<{ has_sample: boolean; creators: number; deals: number } | string>("sample_data_status", { p_org: ws.org.org_id })
      .then((r) => (typeof r === "string" ? JSON.parse(r) as { has_sample: boolean; creators: number; deals: number } : r)),
  });
  if (stand.isLoading) return <main id="main" className="content"><Loading /></main>;
  // Während des Rundgangs mit Coco bleibt die Übersicht stehen (Station 1); danach greift die Weiche wieder
  // K-573: nach beendetem Rundgang („später“ gemerkt) bleibt die Übersicht mit der Liste „So startest du“ stehen
  if (!lauf && zumSchnellstart(stand.data, gemerkt(ws.org.org_id, "spaeter"))) return <Navigate to="/agentur/schnellstart" replace />;
  return ws.can("view_financials") ? <ChefUebersicht ws={ws} /> : <ManagerAnsicht ws={ws} />;
}
